/**
 * Source utility functions
 */

import { LeadSource } from '../types';

/**
 * Normalize source string to LeadSource type
 * Handles formats like: web_form, Web Form, WEB-FORM -> web-form
 */
export const normalizeSource = (source: string | undefined | null): LeadSource => {
  if (!source || typeof source !== 'string') {
    return 'web-form';
  }

  // Convert to lowercase, replace underscores and spaces with hyphens
  let normalized = source
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-')  // underscores and spaces to hyphens
    .replace(/--+/g, '-')      // multiple hyphens to single
    .replace(/^-|-$/g, '');    // trim leading/trailing hyphens

  // Map common variations
  const sourceMap: Record<string, LeadSource> = {
    'webform': 'web-form',
    'web': 'web-form',
    'facebook': 'facebook-form',
    'fb-form': 'facebook-form',
    'fb': 'facebook-form',
    'instagram': 'instagram-form',
    'ig-form': 'instagram-form',
    'ig': 'instagram-form',
    'phone': 'call-in',
    'email': 'web-form',
    'direct': 'web-form',
    'organic': 'web-form',
    'b2b': 'b2b-visit',
    'coldcall': 'cold-call',
    'callin': 'call-in',
  };

  // Check if it's a mapped value
  if (sourceMap[normalized]) {
    return sourceMap[normalized];
  }

  // Check if it's already a valid LeadSource
  const validSources: LeadSource[] = [
    'web-form', 'facebook-form', 'instagram-form', 'ebook',
    'instagram-follow', 'facebook-follow', 'b2b-visit',
    'cold-call', 'call-in', 'referral', 'unspecified'
  ];

  if (validSources.includes(normalized as LeadSource)) {
    return normalized as LeadSource;
  }

  // Default to unspecified if unrecognized
  return 'unspecified';
};

/**
 * Format source name for display
 */
export const formatSourceName = (source: string | LeadSource): string => {
  if (!source || typeof source !== 'string') return 'Unspecified';

  // UTM-style source display names (case-insensitive lookup)
  const utmDisplayNames: Record<string, string> = {
    'kit': 'KIT',
    'facebook': 'Facebook',
    'fb': 'Facebook',
    'google': 'Google',
    'instagram': 'Instagram',
    'ig': 'Instagram',
    'tiktok': 'TikTok',
    'linkedin': 'LinkedIn',
    'twitter': 'Twitter',
    'x': 'X',
    'youtube': 'YouTube',
    'bing': 'Bing',
    'email': 'Email',
    'direct': 'Direct',
    'organic': 'Organic',
    'sales': 'Sales',
    'referral': 'Referral',
    'seo': 'SEO',
    'ppc': 'PPC',
    'cpc': 'CPC',
  };

  const lowered = source.toLowerCase().trim();
  if (utmDisplayNames[lowered]) return utmDisplayNames[lowered];

  // Try standard LeadSource normalization
  const normalizedSource = normalizeSource(source);

  const names: Record<LeadSource, string> = {
    'unspecified': source, // preserve original if unrecognized instead of "Unspecified"
    'web-form': 'Web Form',
    'facebook-form': 'Facebook Form',
    'instagram-form': 'Instagram Form',
    'ebook': 'Ebook',
    'instagram-follow': 'Instagram Follow',
    'facebook-follow': 'Facebook Follow',
    'b2b-visit': 'B2B Visit',
    'cold-call': 'Cold Call',
    'call-in': 'Call-in',
    'referral': 'Referral',
  };
  return names[normalizedSource] || source;
};
